import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../utils/formatting';
import { aiPriceState, aiPriceChipText, aiPriceLabel } from '../../utils/aiPrice';
import { GAP_THRESHOLDS } from '../../utils/lotValue';
import { useDealershipSettings } from '../../contexts/settingsContext';
import useAnchoredTooltip from '../../hooks/useAnchoredTooltip';
import './AiPriceCell.css';

// "5%", "7.5%": whole percents stay whole (dealer thresholds may have a decimal).
const pct = (fraction) => `${Number((fraction * 100).toFixed(1))}%`;

// Header tooltip text for a gap scale (the dealership's; today's by default).
export const aiPriceTip = (t = GAP_THRESHOLDS) => 'Model estimate of the net sale price. The chip compares your asking price with it: '
    + `sage within ±${pct(t.warn)}, amber ${pct(t.warn)}–${pct(t.critical)} above, `
    + `red more than ${pct(t.critical)} above, teal more than ${pct(t.warn)} below. `
    + '"Review" means the model flagged the unit; check it before acting. A dash means no AI price (new units). '
    + 'Sort to bring the most overpriced units to the top.';
export const AI_PRICE_TIP = aiPriceTip(GAP_THRESHOLDS);

// AI price + signed gap chip. The visible parts are aria-hidden; a visually
// hidden sentence (also the hover title) carries the full meaning, so the
// state is never conveyed by colour alone.
function AiPriceCell({ unit }) {
    const { gapThresholds } = useDealershipSettings();
    const state = aiPriceState(unit, gapThresholds);
    const label = aiPriceLabel(unit, gapThresholds);

    return (
        <span className="ai-price-cell" data-testid="ai-price-cell" data-state={state} title={label}>
            {state === 'none' ? (
                <span className="ai-price-none" aria-hidden="true">—</span>
            ) : (
                <>
                    <span className="ai-price-value" data-testid="ai-price-value" aria-hidden="true">
                        {formatCurrency(unit.PredictedPrice)}
                    </span>
                    <span className={`ai-chip ai-chip--${state}`} data-testid="ai-price-chip" aria-hidden="true">
                        {aiPriceChipText(unit, gapThresholds)}
                    </span>
                </>
            )}
            <span className="ai-price-sr">{label}</span>
        </span>
    );
}

AiPriceCell.propTypes = {
    unit: PropTypes.object.isRequired,
};

// Header (i): focusable trigger + aria-describedby role="tooltip", the lot
// value tile / branch table pattern (hover, keyboard focus and tap). Opened by
// state and positioned `fixed` (useAnchoredTooltip, shared with the v2
// Tooltip), so the scrolling table container can't clip it; while open it
// follows the trigger on scroll and resize. Clicks and Enter/Space are stopped
// so the trigger never sorts the column.
export function AiPriceHeaderTip() {
    const tipId = `${useId().replace(/:/g, '')}-ai-price-tip`;
    const { gapThresholds } = useDealershipSettings();
    const { triggerRef, pos, open, close } = useAnchoredTooltip();

    const onKeyDown = (e) => {
        if (e.key === 'Escape') close();
        if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
    };

    return (
        <span
            ref={triggerRef}
            className="ai-price-tip-trigger"
            tabIndex={0}
            role="img"
            aria-label="About AI Price"
            aria-describedby={tipId}
            onMouseEnter={open}
            onMouseLeave={close}
            onFocus={open}
            onBlur={close}
            onKeyDown={onKeyDown}
            onClick={(e) => e.stopPropagation()}
        >
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12" aria-hidden="true" focusable="false">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span
                id={tipId}
                role="tooltip"
                className="ai-price-tip"
                hidden={!pos}
                style={pos ? { top: pos.top, left: pos.left } : undefined}
            >
                {aiPriceTip(gapThresholds)}
            </span>
        </span>
    );
}

export default AiPriceCell;
